/**
 * YouTube Integration Component
 * 
 * Handles YouTube account connection, disconnection, and channel display
 * for multi-tenant application.
 * 
 * Features:
 * - OAuth authentication flow
 * - Channel information display
 * - Connection status indicators
 * - Error handling with user-friendly messages
 * - Loading states
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
  getYouTubeAuthUrl,
  checkYouTubeConnection,
  getYouTubeChannelInfo,
  disconnectYouTube,
  formatNumber
} from '../services/youtubeService';

// YouTube icon component
const YouTubeIcon = ({ className = '' }) => (
  <svg 
    className={className}
    viewBox="0 0 24 24" 
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

function YouTubeIntegration() {
  const { tenant } = useParams();
  const location = useLocation();
  
  // State management
  const [isConnected, setIsConnected] = useState(false);
  const [channelInfo, setChannelInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Get tenant ID
  const getTenantId = useCallback(() => {
    return Number(tenant) || Number(localStorage.getItem('tenantId')) || 1;
  }, [tenant]);

  /**
   * Check connection status and load channel info
   */
  const loadConnectionStatus = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);

      const tenantId = getTenantId();
      
      // Check if YouTube is connected
      const connectionStatus = await checkYouTubeConnection(tenantId, !forceRefresh);
      
      if (connectionStatus.error) {
        throw new Error(connectionStatus.error);
      }

      setIsConnected(connectionStatus.connected);

      // If connected, fetch detailed channel info
      if (connectionStatus.connected) {
        const channelData = await getYouTubeChannelInfo(tenantId, !forceRefresh);
        setChannelInfo(channelData);
      } else {
        setChannelInfo(null);
      }
    } catch (err) {
      console.error('Error loading YouTube connection status:', err);
      setError(err.message || 'Failed to load YouTube connection status');
      setIsConnected(false);
      setChannelInfo(null);
    } finally {
      setLoading(false);
    }
  }, [getTenantId]);

  /**
   * Handle OAuth callback success
   */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    
    if (params.get('youtube') === 'connected') {
      setSuccessMessage('✅ YouTube connected successfully!');
      
      // Clear the success message after 5 seconds
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      
      // Force refresh connection status
      loadConnectionStatus(true);
      
      // Clean up URL
      window.history.replaceState({}, '', location.pathname);
      
      return () => clearTimeout(timer);
    }
  }, [location, loadConnectionStatus]);

  /**
   * Initial load
   */
  useEffect(() => {
    loadConnectionStatus();
  }, [loadConnectionStatus]);

  /**
   * Handle connect button click
   */
  const handleConnect = async () => {
    try {
      setConnecting(true);
      setError(null);

      const tenantId = getTenantId();
      
      // Get OAuth URL
      const result = await getYouTubeAuthUrl(tenantId);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to get authorization URL');
      }

      // Redirect to Google OAuth
      window.location.href = result.url;
    } catch (err) {
      console.error('Error connecting to YouTube:', err);
      setError(err.message || 'Failed to initiate YouTube connection');
      setConnecting(false);
    }
  };

  /**
   * Handle disconnect button click
   */
  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect your YouTube account? You will need to reconnect to post videos.')) {
      return;
    }

    try {
      setDisconnecting(true);
      setError(null);

      const tenantId = getTenantId();
      const result = await disconnectYouTube(tenantId);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to disconnect YouTube');
      }

      setIsConnected(false);
      setChannelInfo(null);
      setSuccessMessage('✅ YouTube disconnected successfully');
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Error disconnecting YouTube:', err);
      setError(err.message || 'Failed to disconnect YouTube');
    } finally {
      setDisconnecting(false);
    }
  };

  /**
   * Refresh connection status
   */
  const handleRefresh = () => {
    loadConnectionStatus(true);
  };

  // Loading state
  if (loading) {
    return (
      <div className="youtube-integration-card">
        <div className="integration-header">
          <YouTubeIcon className="integration-icon youtube-icon" />
          <h3>YouTube</h3>
        </div>
        <div className="integration-body">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading YouTube connection status...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="youtube-integration-card">
      <div className="integration-header">
        <YouTubeIcon className="integration-icon youtube-icon" />
        <h3>YouTube</h3>
        {isConnected && (
          <span className="connection-status connected">
            <span className="status-dot"></span>
            Connected
          </span>
        )}
      </div>

      <div className="integration-body">
        {/* Success Message */}
        {successMessage && (
          <div className="alert alert-success">
            {successMessage}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="alert alert-error">
            <strong>Error:</strong> {error}
            <button 
              className="alert-close" 
              onClick={() => setError(null)}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        )}

        {/* Connected State */}
        {isConnected && channelInfo ? (
          <div className="connected-content">
            <div className="channel-info">
              {/* Channel Thumbnail */}
              {channelInfo.thumbnails?.default?.url && (
                <img 
                  src={channelInfo.thumbnails.default.url} 
                  alt={channelInfo.title}
                  className="channel-thumbnail"
                />
              )}

              {/* Channel Details */}
              <div className="channel-details">
                <h4 className="channel-title">{channelInfo.title}</h4>
                {channelInfo.customUrl && (
                  <p className="channel-url">
                    <a 
                      href={`https://youtube.com/${channelInfo.customUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      youtube.com/{channelInfo.customUrl}
                    </a>
                  </p>
                )}
                
                {/* Channel Statistics */}
                <div className="channel-stats">
                  <div className="stat-item">
                    <span className="stat-label">Subscribers</span>
                    <span className="stat-value">
                      {channelInfo.hiddenSubscriberCount 
                        ? 'Hidden' 
                        : formatNumber(channelInfo.subscriberCount || 0)}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Videos</span>
                    <span className="stat-value">
                      {formatNumber(channelInfo.videoCount || 0)}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Total Views</span>
                    <span className="stat-value">
                      {formatNumber(channelInfo.viewCount || 0)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            {channelInfo.description && (
              <div className="channel-description">
                <p>{channelInfo.description.substring(0, 200)}
                  {channelInfo.description.length > 200 && '...'}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="integration-actions">
              <button
                className="btn btn-secondary"
                onClick={handleRefresh}
                disabled={loading}
              >
                🔄 Refresh
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDisconnect}
                disabled={disconnecting}
              >
                {disconnecting ? 'Disconnecting...' : 'Disconnect'}
              </button>
            </div>
          </div>
        ) : (
          /* Not Connected State */
          <div className="not-connected-content">
            <p className="integration-description">
              Connect your YouTube channel to upload and manage videos directly from your dashboard.
            </p>
            
            <div className="features-list">
              <div className="feature-item">
                <span className="feature-icon">✓</span>
                <span>Upload videos to your channel</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">✓</span>
                <span>Schedule video posts</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">✓</span>
                <span>View video analytics and metrics</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">✓</span>
                <span>Manage video privacy settings</span>
              </div>
            </div>

            <button
              className="btn btn-primary btn-connect"
              onClick={handleConnect}
              disabled={connecting}
            >
              {connecting ? (
                <>
                  <span className="spinner-small"></span>
                  Connecting...
                </>
              ) : (
                <>
                  <YouTubeIcon className="btn-icon" />
                  Connect YouTube
                </>
              )}
            </button>

            <p className="integration-note">
              <strong>Note:</strong> You'll be redirected to Google to authorize access to your YouTube channel.
              We only request the minimum permissions needed to upload videos and view analytics.
            </p>
          </div>
        )}
      </div>

      <style jsx>{`
        .youtube-integration-card {
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          overflow: hidden;
          margin-bottom: 20px;
        }

        .integration-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 20px;
          border-bottom: 1px solid #e5e7eb;
        }

        .integration-icon {
          width: 32px;
          height: 32px;
        }

        .youtube-icon {
          color: #FF0000;
        }

        .integration-header h3 {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 600;
          flex: 1;
        }

        .connection-status {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .connection-status.connected {
          background: #d1fae5;
          color: #065f46;
        }

        .status-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: currentColor;
          animation: pulse 2s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        .integration-body {
          padding: 20px;
        }

        /* Alert Styles */
        .alert {
          padding: 12px 16px;
          border-radius: 6px;
          margin-bottom: 16px;
          position: relative;
        }

        .alert-success {
          background: #d1fae5;
          color: #065f46;
          border: 1px solid #6ee7b7;
        }

        .alert-error {
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #fca5a5;
        }

        .alert-close {
          position: absolute;
          top: 8px;
          right: 8px;
          background: none;
          border: none;
          font-size: 1.5rem;
          line-height: 1;
          cursor: pointer;
          color: inherit;
          opacity: 0.7;
        }

        .alert-close:hover {
          opacity: 1;
        }

        /* Loading Spinner */
        .loading-spinner {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          gap: 12px;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f4f6;
          border-top-color: #FF0000;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .spinner-small {
          display: inline-block;
          width: 16px;
          height: 16px;
          border: 2px solid currentColor;
          border-top-color: transparent;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
          margin-right: 8px;
        }

        /* Connected Content */
        .connected-content {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .channel-info {
          display: flex;
          gap: 16px;
          align-items: flex-start;
        }

        .channel-thumbnail {
          width: 88px;
          height: 88px;
          border-radius: 50%;
          object-fit: cover;
          flex-shrink: 0;
        }

        .channel-details {
          flex: 1;
        }

        .channel-title {
          margin: 0 0 4px 0;
          font-size: 1.125rem;
          font-weight: 600;
        }

        .channel-url {
          margin: 0 0 12px 0;
          font-size: 0.875rem;
        }

        .channel-url a {
          color: #FF0000;
          text-decoration: none;
        }

        .channel-url a:hover {
          text-decoration: underline;
        }

        .channel-stats {
          display: flex;
          gap: 20px;
          flex-wrap: wrap;
        }

        .stat-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .stat-label {
          font-size: 0.75rem;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .stat-value {
          font-size: 1rem;
          font-weight: 600;
          color: #111827;
        }

        .channel-description {
          padding: 12px;
          background: #f9fafb;
          border-radius: 6px;
          font-size: 0.875rem;
          color: #4b5563;
          line-height: 1.5;
        }

        /* Not Connected Content */
        .not-connected-content {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .integration-description {
          margin: 0;
          color: #6b7280;
          line-height: 1.5;
        }

        .features-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .feature-item {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 0.875rem;
          color: #374151;
        }

        .feature-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          background: #d1fae5;
          color: #065f46;
          border-radius: 50%;
          font-weight: bold;
          flex-shrink: 0;
        }

        .integration-note {
          margin: 0;
          padding: 12px;
          background: #fffbeb;
          border: 1px solid #fde68a;
          border-radius: 6px;
          font-size: 0.875rem;
          color: #78350f;
          line-height: 1.5;
        }

        /* Buttons */
        .btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 10px 20px;
          border: none;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-primary {
          background: #FF0000;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background: #CC0000;
        }

        .btn-secondary {
          background: #f3f4f6;
          color: #374151;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #e5e7eb;
        }

        .btn-danger {
          background: #fee2e2;
          color: #991b1b;
        }

        .btn-danger:hover:not(:disabled) {
          background: #fecaca;
        }

        .btn-connect {
          align-self: flex-start;
          font-size: 1rem;
          padding: 12px 24px;
        }

        .btn-icon {
          width: 20px;
          height: 20px;
        }

        .integration-actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        /* Responsive Design */
        @media (max-width: 640px) {
          .channel-info {
            flex-direction: column;
            align-items: center;
            text-align: center;
          }

          .channel-stats {
            justify-content: center;
          }

          .integration-actions {
            flex-direction: column;
          }

          .btn {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
}

export default YouTubeIntegration;
